import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from '@/components/ui';
import { customerKeys, customerService } from '@/services/customers';
import {
  buildAdvanceTabId,
  buildClientTabId,
  useWorkspaceTabsStore,
} from '@/stores';
import type { WorkspaceTab as WorkspaceTabModel } from '@/stores';
import { WorkspaceTab } from './WorkspaceTab';

const HOVER_PREFETCH_DELAY_MS = 300;

export function WorkspaceTabsBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const tabs = useWorkspaceTabsStore(s => s.tabs);
  const activate = useWorkspaceTabsStore(s => s.activate);
  const closeTab = useWorkspaceTabsStore(s => s.closeTab);

  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleTabHoverStart = useCallback(
    (tab: WorkspaceTabModel) => {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => {
        if (tab.type === 'client') {
          queryClient.prefetchQuery({
            queryKey: customerKeys.detail(tab.entityId),
            queryFn: () => customerService.getCustomer(tab.entityId),
            staleTime: 5 * 60 * 1000,
          });
          return;
        }
        if (tab.type === 'advance') {
          // Advance routes carry the parent customerId — parse it so we can
          // warm both the loan and the parent customer (used by the breadcrumb
          // and header on AdvanceDetailPage).
          const match = tab.route.match(
            /^\/clients\/([^/?#]+)\/advances\/[^/?#]+/
          );
          const parentCustomerId = match?.[1];
          if (!parentCustomerId) return;
          queryClient.prefetchQuery({
            queryKey: customerKeys.loan(parentCustomerId, tab.entityId),
            queryFn: () =>
              customerService.getLoan(parentCustomerId, tab.entityId),
            staleTime: 5 * 60 * 1000,
          });
          queryClient.prefetchQuery({
            queryKey: customerKeys.detail(parentCustomerId),
            queryFn: () => customerService.getCustomer(parentCustomerId),
            staleTime: 5 * 60 * 1000,
          });
        }
      }, HOVER_PREFETCH_DELAY_MS);
    },
    [queryClient]
  );

  const handleTabHoverEnd = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
  }, []);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  if (tabs.length === 0) return null;

  // Visually-active tab derives from the URL so it stays correct when the user
  // navigates via sidebar, browser back/forward, or direct URL. Advance routes
  // are nested under /clients/:cid/advances/:aid — check those first so the
  // bar highlights the advance tab, not the parent client tab.
  const advanceMatch = location.pathname.match(
    /^\/clients\/[^/?#]+\/advances\/([^/?#]+)/
  );
  const clientMatch = !advanceMatch
    ? location.pathname.match(/^\/clients\/([^/?#]+)/)
    : null;

  const visualActiveId = advanceMatch
    ? buildAdvanceTabId(advanceMatch[1] as string)
    : clientMatch
      ? buildClientTabId(clientMatch[1] as string)
      : null;

  const currentUrl = location.pathname + location.search;

  const handleActivate = (id: string, route: string) => {
    activate(id);
    if (route !== currentUrl) {
      navigate(route);
    }
  };

  const handleClose = (id: string) => {
    const wasViewing = visualActiveId === id;
    const { nextRoute } = closeTab(id);
    if (wasViewing) {
      navigate(nextRoute);
    }
  };

  const handleNewTab = () => {
    navigate('/clients');
  };

  return (
    <div
      className="bg-white border-b border-neutral-100 sticky top-[56px] z-30"
      role="tablist"
      aria-label="Open client workspaces"
    >
      <div className="flex items-center gap-2 px-4 md:px-6 py-1.5 overflow-x-auto scrollbar-none">
        {tabs.map(tab => (
          <WorkspaceTab
            key={tab.id}
            label={tab.label}
            active={visualActiveId === tab.id}
            onActivate={() => handleActivate(tab.id, tab.route)}
            onClose={() => handleClose(tab.id)}
            onHoverStart={() => handleTabHoverStart(tab)}
            onHoverEnd={handleTabHoverEnd}
          />
        ))}
        <button
          type="button"
          onClick={handleNewTab}
          aria-label="New tab — back to Clients"
          className="w-7 h-7 inline-flex items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors shrink-0"
        >
          <PlusIcon size={16} />
        </button>
      </div>
    </div>
  );
}
